import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import "react-datepicker/dist/react-datepicker.css";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useQuery} from "@tanstack/react-query";
import adrMonitoringService from "../../../../services/web/adr-monitoring-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import projectUtils from "../../../../utils/project-utils.js";
import laneAliasService from "../../../../services/web/lane-alias-service.js";
import adrCodeService from "../../../../services/web/adr-code-service.js";
import Loader from "../../../../components/template/loader.jsx";
import AdrCellRenderer from "../../../../components/adr-cell-renderer/adr-cell-renderer.jsx";
import DaysOfWeekCellRenderer, {dayMap} from "../../../../components/days-of-week-cell-renderer/days-of-week-cell-renderer.jsx";
import TimeRangeCellRenderer from "../../../../components/time-range-cell-renderer/time-range-cell-renderer.jsx";
import LaneCountCellRenderer from "../../../../components/lane-count-cell-renderer/lane-count-cell-renderer.jsx";
import ExpiryDateCellRenderer from "../../../../components/expiry-date-cell-renderer/expiry-date-cell-renderer.jsx";

const initialFilters = {
    lanes: [],
    specialTrigger: "ADR",
    description: ""
}

const AdrMonitoringList = () => {

    const navigate = navigateHook();
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState(initialFilters);
    const [laneOptions, setLaneOptions] = useState([])
    const [laneMapOptions, setLaneMapOptions] = useState({})

    const {data, isLoading: isSearching} = useQuery({
        queryFn: () => {
            return adrMonitoringService.list(filter)
        },
        queryKey: ["list", {filter}]
    });

    const {data: lanes, isLoading: isLoadingLanes} = useQuery({
        queryFn: () => {
            return laneAliasService.adrLanes()
        },
        queryKey: ["lanes", {}]
    });

    useEffect(() => {
        if(lanes){
            const toSelectLanes = lanes?.map((lane) => {return {
                value: lane?.code,
                label: lane?.formatted_description
            }})

            const laneMap = Object.fromEntries(
                toSelectLanes.map(d => [d.value, d])
            );

            setLaneOptions(toSelectLanes);
            setLaneMapOptions(laneMap);

        }

    }, [lanes]);

    const apply = useCallback(() => {

        setFilter({
                ...filterValues,
                "lanes": filterValues.lanes.map(lane => lane.value),
                "specialTriggerDetails": {
                    "data": {
                        "description": filterValues?.description
                    },
                    "type": "ADR"
                },
            }
        );

    }, [filterValues])


    const clear = () => {
        setFilterValues(initialFilters)
        setFilter(initialFilters)
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    const onChange = ({target}) => {
        let {id, value} = target;
        setFilterValues(prev => ({...prev, [id]: value}));
    }

    const handleSelect = (id, value) => setFilterValues(prev => ({...prev, [id]: value}));

    const onCellClicked = ({ data, event }) => {
        if(!data?.id) return;

        let urlDev = `/adr-monitoring/form/${data.id}`;
        let isBlank = !!event.ctrlKey;

        navigate(urlDev, isBlank);
    };

    const gridRef = useRef(null);

    const columnDefs = useMemo(
        () => [
            {
                headerName: "Painéis de segurança",
                field: "specialTriggerDetails",
                valueFormatter: ({ value }) => (value == null ? null : value?.adrData.map(i => adrCodeService.formatAdrCode(i.code)).join(", ")),
                cellRenderer: AdrCellRenderer,
                comparator: AgGridUtils.nullLastComparator,
            },
            {
                headerName: "Motivo do monitoramento",
                flex: 1,
                field: "specialTriggerDetails.description",
                comparator: AgGridUtils.nullLastComparator,
                cellStyle: { display: 'flex', alignItems: 'center' },
            },
            {
                headerName: "Câmeras",
                field: "lanes",
                valueFormatter: ({ value }) => (value == null ? null : value?.length),
                cellRenderer: LaneCountCellRenderer,
                comparator: (_lanesA, _lanesB, nodeA, nodeB) => (
                    (nodeA.data?.lanes?.length ?? 0) - (nodeB.data?.lanes?.length ?? 0)
                ),
                maxWidth: 100,
            },
            {
                headerName: "Dias da semana",
                field: "daysOfWeek",
                valueFormatter: ({ value }) => (value == null ? null : value.map(i => dayMap[i]?.label).join(", ")),
                cellRenderer: DaysOfWeekCellRenderer,
                comparator: AgGridUtils.nullLastComparator,
            },
            {
                headerName: "Horário",
                valueGetter: ({ data }) => data ? `${data.timeIni ?? '--'} - ${data.timeEnd ?? '--'}` : null,
                cellRenderer: TimeRangeCellRenderer,
                comparator: AgGridUtils.nullLastComparator,
                maxWidth: 140,
            },
            {
                headerName: "Data de expiração",
                field: "dateExpiredMonitoring",
                flex: 1,
                maxWidth: 200,
                valueFormatter: ({ value }) => projectUtils.formatDate(value),
                cellRenderer: ExpiryDateCellRenderer,
                comparator: AgGridUtils.nullLastComparator,
            },
            {
                headerName: "Ativo",
                field: "status",
                flex: 1,
                maxWidth: 75,
                comparator: AgGridUtils.nullLastComparator,
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }
            }
        ],
        [data, laneMapOptions]
    );

    const newMonitoring = () => {
        const urlDev = "/adr-monitoring/form";
        navigate(urlDev);
    };

    return (
        <Template isLoading={isLoadingLanes || isSearching}>
            <Template.Header>
                <Template.Title browserTitle={'Produtos perigosos - Monitoramentos'}>Monitoramentos</Template.Title>
                <Button onClick={newMonitoring}>
                    <FaPlus /> Adicionar monitoramento
                </Button>
            </Template.Header>
            <Template.Body className={`p-1 flex flex-col gap-2`}>
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <InputForm classNameWrapper="flex-1" id="description" value={filterValues?.description ?? ""} label="Motivo do monitoramento" onChange={onChange}/>
                            <SelectComponent
                                id="lanes"
                                classNameWrapper="flex-1"
                                isMulti={true}
                                value={filterValues.lanes}
                                options={laneOptions ?? []}
                                label={"Câmeras"}
                                onChange={(value) => handleSelect("lanes", value)}
                                isClearable={false}
                            />
                            <div className="flex gap-2">
                                <Button
                                    target="primary-outline"
                                    title="Limpar"
                                    onClick={clear}
                                >
                                    <FaEraser/>
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={apply}
                                >
                                    <FaSearch/>
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card className="flex-1 min-h-0">
                    <Card.Body className="h-full w-full min-h-0">
                        <div className="h-[calc(100vh-153px)]  w-full min-h-0 aggrid-row-pointer">
                            {!isLoadingLanes && !isSearching && (
                                <AgGridReact
                                    ref={gridRef}
                                    loading={isLoadingLanes || isSearching}
                                    rowData={data?.content ?? []}
                                    columnDefs={columnDefs}
                                    rowHeight={65}
                                    pagination
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    suppressCellFocus={true}
                                    onCellClicked={onCellClicked}
                                    animateRows={false}
                                    gridOptions={{ localeText: AgGridUtils.localization() }}
                                    theme={AgGridUtils.generalTheme()}
                                />
                            )}
                            {isLoadingLanes || isSearching && (
                                <Loader />
                            )}
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AdrMonitoringList;
